/**
 * MultiSelectFilter Component
 * 
 * Checkbox list for selecting multiple values (stages, tags, types, etc.)
 */

import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Checkbox,
  Text,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Button,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi';

/**
 * MultiSelectFilter - Checkbox dropdown for multi-value selection
 */
export default function MultiSelectFilter({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select values...',
  maxDisplay = 2,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  // Filter options by search term
  const filteredOptions = options.filter(opt => 
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Handle checkbox toggle
  const handleToggle = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };
  
  // Handle select all / clear all
  const handleSelectAll = () => {
    onChange(options.map(o => o.value));
  };
  
  const handleClearAll = () => {
    onChange([]);
  };
  
  // Get display text for button
  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length <= maxDisplay) {
      return value.map(v => options.find(o => o.value === v)?.label || v).join(', ');
    }
    const displayLabels = value.slice(0, maxDisplay)
      .map(v => options.find(o => o.value === v)?.label || v)
      .join(', ');
    return `${displayLabels} +${value.length - maxDisplay} more`;
  };
  
  return (
    <Popover placement="bottom-start" closeOnBlur={true} isLazy>
      <PopoverTrigger>
        <Button
          size="sm"
          variant="outline"
          rightIcon={<FiChevronDown />}
          fontWeight="normal"
          minW="180px"
          justifyContent="space-between"
          textAlign="left"
        >
          <Text noOfLines={1} fontSize="sm">
            {getDisplayText()}
          </Text>
        </Button>
      </PopoverTrigger>
      {/* 
        Note: Removed Portal wrapper so Popover renders within parent's 
        stacking context. This ensures it works properly inside Modals.
        Using zIndex={2000} as extra precaution.
      */}
      <PopoverContent width="280px" bg={bgColor} borderColor={borderColor} zIndex={2000}>
        <PopoverBody p={2}>
          <VStack align="stretch" spacing={2}>
            {/* Search */}
            {options.length > 5 && (
              <Input
                size="sm"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}
            
            {/* Select All / Clear All */}
            <HStack justify="space-between" px={1}>
              <Button size="xs" variant="link" onClick={handleSelectAll}>
                Select all
              </Button>
              <Button size="xs" variant="link" onClick={handleClearAll}>
                Clear
              </Button>
            </HStack>
            
            {/* Options List */}
            <Box maxH="200px" overflowY="auto">
              <VStack align="stretch" spacing={1}>
                {filteredOptions.map(option => (
                  <Checkbox
                    key={option.value}
                    isChecked={value.includes(option.value)}
                    onChange={() => handleToggle(option.value)}
                    size="sm"
                  >
                    <Text fontSize="sm">{option.label}</Text>
                  </Checkbox>
                ))}
                {filteredOptions.length === 0 && (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={2}>
                    No options found
                  </Text>
                )}
              </VStack>
            </Box>
            
            {/* Selected count */}
            {value.length > 0 && (
              <Badge colorScheme="blue" alignSelf="flex-start">
                {value.length} selected
              </Badge>
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
